/**
 * STEP3: パッケージオプション選択コンポーネント
 * 
 * 設計書に基づいて以下の3つのフローに対応：
 * 1. フリーズドライ: インナーパッケージデザイン/表ラベル + 外箱 + 外箱デザイン
 * 2. 濃縮液: 外箱 + 外箱デザイン + 表ラベル
 * 3. ストレートコーヒー: 外箱 + 外箱デザイン + 表ラベル
 */

import React, { useState, useEffect } from 'react';
import { useOrderFlowStore } from '../../../store/orderFlowStore';
import { useTranslations } from '../../../hooks/useTranslations';
import { useOptionIcons } from '../../../hooks/useOptionIcons';
import { useTemporaryRestrictions } from '../../../hooks/useTemporaryRestrictions';
import SelectionGroup from '../../../components/molecules/SelectionGroup/SelectionGroup';
import { ProductType, PackageType } from '../../../types/orderFlow';
import styles from './SelectPackageOptions.module.scss';

// 選択肢のオプション型
interface SelectionOption {
  value: string;
  label: string;
  description: string;
  priceInfo?: string;
  image?: string;
  accordionContent?: React.ReactNode;
  disabled?: boolean;
  disabledMessage?: string;
}

export const SelectPackageOptions: React.FC = () => {
  const {
    productType,
    packageType,
    needsInnerDesign,
    needsFrontLabel,
    needsOuterBox,
    needsOuterBoxDesign,
    userSelections,
    setNeedsInnerDesign,
    setNeedsFrontLabel,
    setNeedsOuterBox,
    setNeedsOuterBoxDesign,
  } = useOrderFlowStore();

  const t = useTranslations();
  const { getOptionIcon, isOptionIconsAvailable } = useOptionIcons();
  const [errors, setErrors] = useState<string[]>([]);
  const {
    isInnerDesignRequiredDisabled,
    isOuterBoxRequiredDisabled,
    isFrontLabelDisabled,
    disabledMessage,
  } = useTemporaryRestrictions();

  // バリデーション
  const validateSelections = (): boolean => {
    const newErrors: string[] = [];

    if (!productType) {
              newErrors.push(t.selectPackage_productTypeNotSelected);
      setErrors(newErrors);
      return false;
    }

    // フリーズドライの場合の特別なバリデーション
    if (productType === 'freeze-dried') {
      // スティックの場合：インナーデザイン要否の選択が必要
      if (packageType === 'stick') {
        // インナーデザインの選択状況はbooleanなので、特別なバリデーションは不要
      }
      // キューブの場合：インナー表ラベル要否の選択が必要
      if (packageType === 'cube') {
        // インナー表ラベルの選択状況はbooleanなので、特別なバリデーションは不要
      }
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  // バリデーション結果をエクスポートして外部から参照できるようにする
  // OrderFlowContainerで使用される

  // 制約に基づく自動選択
  // 既に非活性なオプションが選択されている場合もフォールバック値を設定
  useEffect(() => {
    if (!productType) return;

    // フリーズドライ・スティック: インナーデザイン非活性 → 不要自動選択
    // 「必要」が選択されている場合も強制的に「不要」に切り替え
    if (productType === 'freeze-dried' && packageType === 'stick') {
      if (isInnerDesignRequiredDisabled) {
        if (needsInnerDesign || !userSelections.hasSelectedInnerDesign) {
          setNeedsInnerDesign(false);
        }
      }
    }

    // 全タイプ: 外箱非活性 → 不要自動選択
    // 「必要」が選択されている場合も強制的に「不要」に切り替え
    if (isOuterBoxRequiredDisabled) {
      if (needsOuterBox || !userSelections.hasSelectedOuterBox) {
        setNeedsOuterBox(false);
      }
    }

    // 表ラベル選択非活性 → 不要自動選択
    // 濃縮液・ストレート: 全パッケージ対象
    // フリーズドライ: キューブのみ対象
    // 「必要」が選択されている場合も強制的に「不要」に切り替え
    if (isFrontLabelDisabled) {
      if (
        productType === 'liquid-concentrate' ||
        productType === 'straight-coffee' ||
        (productType === 'freeze-dried' && packageType === 'cube')
      ) {
        if (needsFrontLabel || !userSelections.hasSelectedFrontLabel) {
          setNeedsFrontLabel(false);
        }
      }
    }
  }, [
    productType,
    packageType,
    isInnerDesignRequiredDisabled,
    isOuterBoxRequiredDisabled,
    isFrontLabelDisabled,
    needsInnerDesign,
    needsOuterBox,
    needsFrontLabel,
    userSelections.hasSelectedFrontLabel,
    userSelections.hasSelectedInnerDesign,
    userSelections.hasSelectedOuterBox,
    setNeedsInnerDesign,
    setNeedsOuterBox,
    setNeedsFrontLabel,
  ]);

  // フリーズドライ用のインナーパッケージデザイン選択肢
  const getInnerDesignOptions = (): SelectionOption[] => {
    return [
      {
        value: 'required',
        label: t.selectInnerDesign_required_label,
        description: t.selectInnerDesign_required_desc,
        priceInfo: t.priceDisplay_optionAdditional,
        image: isOptionIconsAvailable() ? getOptionIcon('fd', 'inner-design-required') : undefined,
        // インナーパッケージデザイン「必要」を非活性
        disabled: isInnerDesignRequiredDisabled,
        disabledMessage: isInnerDesignRequiredDisabled ? disabledMessage : undefined,
      },
      {
        value: 'not-required',
        label: t.selectInnerDesign_notRequired_label,
        description: t.selectInnerDesign_notRequired_desc,
        image: isOptionIconsAvailable() ? getOptionIcon('fd', 'inner-design-not-required') : undefined,
      }
    ];
  };

  // フリーズドライ（キューブ）用のインナー表ラベル選択肢
  const getInnerFrontLabelOptions = (): SelectionOption[] => {
    return [
      {
        value: 'required',
        label: t.selectInnerFrontLabel_required_label,
        description: t.selectInnerFrontLabel_required_desc,
        priceInfo: t.priceDisplay_optionAdditional,
        image: isOptionIconsAvailable() ? getOptionIcon('fd', 'inner-front-label-required') : undefined,
        disabled: isFrontLabelDisabled,
        disabledMessage: isFrontLabelDisabled ? disabledMessage : undefined,
      },
      {
        value: 'not-required',
        label: t.selectInnerFrontLabel_notRequired_label,
        description: t.selectInnerFrontLabel_notRequired_desc,
        image: isOptionIconsAvailable() ? getOptionIcon('fd', 'inner-front-label-not-required') : undefined,
      }
    ];
  };

  // 外箱選択肢（全フロー共通）
  const getOuterBoxOptions = (): SelectionOption[] => {
    const productPrefix = productType === 'freeze-dried' ? 'fd' : 
                         productType === 'liquid-concentrate' ? 'nk' : 'st';
    
    return [
      {
        value: 'required',
        label: t.selectOuterBox_required_label,
        description: t.selectOuterBox_required_desc,
        priceInfo: t.priceDisplay_optionAdditional,
        image: isOptionIconsAvailable() ? getOptionIcon(productPrefix, 'outer-box-required') : undefined,
        // 外箱「必要」を非活性
        disabled: isOuterBoxRequiredDisabled,
        disabledMessage: isOuterBoxRequiredDisabled ? disabledMessage : undefined,
        // アコーディオンコンテンツ：外箱デザイン選択
        accordionContent: (
          <div style={{ marginTop: '16px' }}>
            <SelectionGroup
              title={t.selectOuterBoxDesign_title}
              options={getOuterBoxDesignOptions()}
              selectedValue={getCurrentOuterBoxDesignValue()}
              onChange={handleOuterBoxDesignChange}
              name="outerBoxDesign"
              isNested={true}
              showQuestionIcon={true}
              helpText={t.help_selectOuterBoxDesign}
            />
          </div>
        )
      },
      {
        value: 'not-required',
        label: t.selectOuterBox_notRequired_label,
        description: t.selectOuterBox_notRequired_desc,
        image: isOptionIconsAvailable() ? getOptionIcon(productPrefix, 'outer-box-not-required') : undefined,
      }
    ];
  };

  // 外箱デザイン選択肢
  const getOuterBoxDesignOptions = (): SelectionOption[] => {
    const productPrefix = productType === 'freeze-dried' ? 'fd' : 
                         productType === 'liquid-concentrate' ? 'nk' : 'st';
    
    return [
      {
        value: 'required',
        label: t.selectOuterBoxDesign_required_label,
        description: t.selectOuterBoxDesign_required_desc,
        priceInfo: t.priceDisplay_optionAdditional,
        image: isOptionIconsAvailable() ? getOptionIcon(productPrefix, 'outer-design-required') : undefined,
      },
      {
        value: 'not-required',
        label: t.selectOuterBoxDesign_notRequired_label,
        description: t.selectOuterBoxDesign_notRequired_desc,
        image: isOptionIconsAvailable() ? getOptionIcon(productPrefix, 'outer-design-not-required') : undefined,
      }
    ];
  };

  // 表ラベル選択肢（濃縮液・ストレート用）
  const getFrontLabelOptions = (): SelectionOption[] => {
    const productPrefix = productType === 'liquid-concentrate' ? 'nk' : 'st';
    
    return [
      {
        value: 'required',
        label: t.selectFrontLabel_required_label,
        description: t.selectFrontLabel_required_desc,
        priceInfo: t.priceDisplay_optionAdditional,
        image: isOptionIconsAvailable() ? getOptionIcon(productPrefix, 'front-label-required') : undefined,
        disabled: isFrontLabelDisabled,
        disabledMessage: isFrontLabelDisabled ? disabledMessage : undefined,
      },
      {
        value: 'not-required',
        label: t.selectFrontLabel_notRequired_label,
        description: t.selectFrontLabel_notRequired_desc,
        image: isOptionIconsAvailable() ? getOptionIcon(productPrefix, 'front-label-not-required') : undefined,
      }
    ];
  };

  // ハンドラー関数
  const handleInnerDesignChange = (value: string) => {
    setNeedsInnerDesign(value === 'required');
  };

  const handleInnerFrontLabelChange = (value: string) => {
    setNeedsFrontLabel(value === 'required');
  };

  const handleFrontLabelChange = (value: string) => {
    setNeedsFrontLabel(value === 'required');
  };

  const handleOuterBoxChange = (value: string) => {
    const isRequired = value === 'required';
    setNeedsOuterBox(isRequired);
    
    // 外箱が不要な場合は外箱デザインも自動的に不要にする
    if (!isRequired) {
      setNeedsOuterBoxDesign(false);
    }
  };

  const handleOuterBoxDesignChange = (value: string) => {
    setNeedsOuterBoxDesign(value === 'required');
  };

  // 現在の選択値を文字列形式で取得（初期状態では空文字を返して未選択状態にする）
  const getCurrentInnerDesignValue = () => {
    if (!userSelections.hasSelectedInnerDesign) return '';
    return needsInnerDesign ? 'required' : 'not-required';
  };
  const getCurrentInnerFrontLabelValue = () => {
    if (!userSelections.hasSelectedFrontLabel) return '';
    return needsFrontLabel ? 'required' : 'not-required';
  };
  const getCurrentOuterBoxValue = () => {
    if (!userSelections.hasSelectedOuterBox) return '';
    return needsOuterBox ? 'required' : 'not-required';
  };
  const getCurrentOuterBoxDesignValue = () => {
    if (!userSelections.hasSelectedOuterBoxDesign) return '';
    return needsOuterBoxDesign ? 'required' : 'not-required';
  };
  // 表ラベル用の選択値取得関数を追加
  const getCurrentFrontLabelValue = () => {
    if (!userSelections.hasSelectedFrontLabel) return '';
    return needsFrontLabel ? 'required' : 'not-required';
  };

  // フロー別の表示制御
  const shouldShowInnerDesign = () => {
    return productType === 'freeze-dried' && packageType === 'stick';
  };

  const shouldShowInnerFrontLabel = () => {
    return productType === 'freeze-dried' && packageType === 'cube';
  };

  const shouldShowFrontLabel = () => {
    return productType === 'liquid-concentrate' || productType === 'straight-coffee';
  };

  return (
    <div className={styles.container}>
      {/* <div className={styles.header}>
        <h2 className={styles.title}>
          {t.selectPackageOptions_title}
        </h2>
      </div> */}

      <div className={styles.content}>
        {/* フリーズドライ（スティック）用：インナーパッケージデザイン */}
        {shouldShowInnerDesign() && (
          <div className={styles.section}>
            <SelectionGroup
              title={t.selectInnerDesign_title}
              options={getInnerDesignOptions()}
              selectedValue={getCurrentInnerDesignValue()}
              onChange={handleInnerDesignChange}
              name="innerDesign"
              showQuestionIcon={true}
              helpText={t.help_selectInnerDesign}
            />
          </div>
        )}

        {/* フリーズドライ（キューブ）用：インナー表ラベル */}
        {shouldShowInnerFrontLabel() && (
          <div className={styles.section}>
            <SelectionGroup
              title={t.selectInnerFrontLabel_title}
              options={getInnerFrontLabelOptions()}
              selectedValue={getCurrentInnerFrontLabelValue()}
              onChange={handleInnerFrontLabelChange}
              name="innerFrontLabel"
              showQuestionIcon={true}
              helpText={t.help_selectInnerFrontLabel}
            />
          </div>
        )}

        {/* 外箱選択（全フロー共通）- アコーディオンで外箱デザイン選択も含む */}
        <div className={styles.section}>
          <SelectionGroup
            title={t.selectOuterBox_title}
            options={getOuterBoxOptions()}
            selectedValue={getCurrentOuterBoxValue()}
            onChange={handleOuterBoxChange}
            name="outerBox"
            showQuestionIcon={true}
            helpText={t.help_selectOuterBox}
          />
        </div>

        {/* 濃縮液・ストレート用：表ラベル */}
        {shouldShowFrontLabel() && (
          <div className={styles.section}>
            <SelectionGroup
              title={t.selectFrontLabel_title}
              options={getFrontLabelOptions()}
              selectedValue={getCurrentFrontLabelValue()}
              onChange={handleFrontLabelChange}
              name="frontLabel"
              showQuestionIcon={true}
              helpText={t.help_selectFrontLabel}
            />
          </div>
        )}

        {/* エラー表示 */}
        {errors.length > 0 && (
          <div className={styles.errors}>
            {errors.map((error, index) => (
              <p key={index} className={styles.error}>
                {error}
              </p>
            ))}
          </div>
        )}

        {/* ナビゲーションは共通のFlowFooterを使用するため削除 */}
      </div>
    </div>
  );
}; 
